import { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'sonner';
import {
  Plus, Search, Edit2, Trash2, Users, X, Download, History,
  CheckCircle, Bus, MapPin, Calendar, Package, AlertCircle, UserCheck,
  Activity, ChevronDown
} from 'lucide-react';
import api from '../api';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import { moduleEnabled } from '../config/modules';

const EMPTY = { nome: '', telefone: '', email: '', documento: '', tipo: 'PF', observacoes: '' };
const CONDICIONAMENTO = ['Iniciante', 'Intermediário', 'Avançado'];

function formatarDocumento(v) {
  const n = v.replace(/\D/g, '');
  if (n.length <= 11) {
    if (n.length <= 3) return n;
    if (n.length <= 6) return `${n.slice(0,3)}.${n.slice(3)}`;
    if (n.length <= 9) return `${n.slice(0,3)}.${n.slice(3,6)}.${n.slice(6)}`;
    return `${n.slice(0,3)}.${n.slice(3,6)}.${n.slice(6,9)}.${n.slice(9,11)}`;
  }
  const d = n.slice(0, 14);
  if (d.length <= 2) return d;
  if (d.length <= 5) return `${d.slice(0,2)}.${d.slice(2)}`;
  if (d.length <= 8) return `${d.slice(0,2)}.${d.slice(2,5)}.${d.slice(5)}`;
  if (d.length <= 12) return `${d.slice(0,2)}.${d.slice(2,5)}.${d.slice(5,8)}/${d.slice(8)}`;
  return `${d.slice(0,2)}.${d.slice(2,5)}.${d.slice(5,8)}/${d.slice(8,12)}-${d.slice(12)}`;
}

const statusCls = {
  confirmada: 'bg-emerald-100 text-emerald-700',
  pendente: 'bg-amber-100 text-amber-700',
  cancelada: 'bg-red-100 text-red-700',
};
const statusLabel = { confirmada: 'Confirmada', pendente: 'Pendente', cancelada: 'Cancelada' };

function Badge({ tipo }) {
  return (
    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${tipo === 'PJ' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
      {tipo === 'PJ' ? 'Pessoa Jurídica' : 'Pessoa Física'}
    </span>
  );
}

function HistoricoModal({ clienteId, onClose }) {
  const { branding } = useTenant();
  const showEncomendas = moduleEnabled(branding, 'encomendas');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const fmt = (v) => v?.toLocaleString('pt-BR', { minimumFractionDigits: 2 }) ?? '0,00';
  const fmtDate = (d) => d ? new Date(d + 'T00:00:00').toLocaleDateString('pt-BR') : '—';
  const sEncMap = { recebida: 'bg-blue-100 text-blue-700', em_transito: 'bg-amber-100 text-amber-700', entregue: 'bg-emerald-100 text-emerald-700', cancelada: 'bg-red-100 text-red-700' };
  const sEncLabel = { recebida: 'Recebida', em_transito: 'Em Trânsito', entregue: 'Entregue', cancelada: 'Cancelada' };

  useEffect(() => {
    api.get(`/clientes/${clienteId}/historico`)
      .then(r => setData(r.data))
      .catch(() => toast.error('Erro ao carregar histórico'))
      .finally(() => setLoading(false));
  }, [clienteId]);

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-slate-100 sticky top-0 bg-white z-10">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-orange-100 flex items-center justify-center">
              <History className="w-4 h-4 text-orange-600" />
            </div>
            <div>
              <h2 className="font-bold text-slate-800 text-base">Histórico de Reservas</h2>
              {data && <p className="text-slate-500 text-xs">{data.cliente.nome}</p>}
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100">
            <X className="w-5 h-5" />
          </button>
        </div>

        {loading ? (
          <div className="p-10 text-center text-slate-400 text-sm">Carregando histórico...</div>
        ) : !data ? (
          <div className="p-10 text-center text-slate-400 text-sm">Erro ao carregar dados</div>
        ) : (
          <div className="p-5 space-y-5">
            {/* Resumo */}
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
              {[
                { value: data.resumo.total, label: 'Reservas', cls: 'bg-slate-50 text-slate-800' },
                { value: data.resumo.confirmadas, label: 'Confirmadas', cls: 'bg-emerald-50 text-emerald-700' },
                { value: data.resumo.canceladas, label: 'Canceladas', cls: 'bg-red-50 text-red-600' },
                { value: data.resumo.checkins_realizados, label: 'Embarques', cls: 'bg-blue-50 text-blue-700' },
              ].map(({ value, label, cls }) => (
                <div key={label} className={`rounded-xl p-3 text-center ${cls}`}>
                  <p className="text-2xl font-bold">{value}</p>
                  <p className="text-xs mt-0.5 opacity-70">{label}</p>
                </div>
              ))}
              <div className="bg-orange-50 rounded-xl p-3 text-center col-span-2 sm:col-span-1">
                <p className="text-lg font-bold text-orange-700">R$ {fmt(data.resumo.total_gasto)}</p>
                <p className="text-xs text-orange-600 mt-0.5">Total gasto</p>
              </div>
            </div>

            {/* Encomendas */}
            {showEncomendas && data.encomendas?.length > 0 && (
              <div>
                <h3 className="font-bold text-slate-700 text-sm mb-2 flex items-center gap-2">
                  <Package className="w-4 h-4 text-slate-400" />
                  Encomendas ({data.resumo.total_encomendas}) — {data.resumo.encomendas_entregues} entregues
                </h3>
                <div className="space-y-2">
                  {data.encomendas.map((e, i) => (
                    <div key={e.id || i} className="flex items-start gap-3 p-3 bg-slate-50 rounded-xl border border-slate-200">
                      <Package className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-slate-800 text-sm">{e.codigo_interno}</p>
                        <p className="text-xs text-slate-500">{e.eh_remetente ? 'Remetente' : 'Destinatário'} · {e.viagem?.titulo || '—'} · {fmtDate(e.viagem?.data)}</p>
                      </div>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium flex-shrink-0 ${sEncMap[e.status] || 'bg-slate-100 text-slate-700'}`}>
                        {sEncLabel[e.status] || e.status}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Reservas */}
            {data.reservas.length === 0 ? (
              <div className="flex flex-col items-center py-10 text-slate-400">
                <Bus className="w-10 h-10 mb-2 opacity-20" />
                <p className="text-sm">Nenhuma reserva encontrada</p>
              </div>
            ) : (
              <div className="space-y-2.5">
                {data.reservas.map((r) => (
                  <div key={r.id} className="bg-white border border-slate-200 rounded-xl p-4 hover:border-orange-200 transition-colors">
                    <div className="flex items-start justify-between gap-3">
                      <div className="w-11 h-11 rounded-xl flex flex-col items-center justify-center flex-shrink-0"
                        style={{ background: 'var(--brand-secondary, #d72d21)' }}>
                        <span className="text-white font-bold text-lg leading-none">{r.assento}</span>
                        <span className="text-white/70 text-[9px] leading-none">ASSENTO</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-slate-800 text-sm truncate">{r.viagem?.titulo || 'Viagem não encontrada'}</p>
                        {r.viagem && (
                          <>
                            <div className="flex items-center gap-1 text-slate-500 text-xs mt-0.5">
                              <MapPin className="w-3 h-3 flex-shrink-0" style={{ color: 'var(--brand-secondary, #d72d21)' }} />
                              <span>{r.viagem.origem} → {r.viagem.destino}</span>
                            </div>
                            <div className="flex items-center gap-1 text-slate-400 text-xs mt-1">
                              <Calendar className="w-3 h-3" />
                              <span>{fmtDate(r.viagem.data)}</span>
                              {r.viagem.horario && <span className="font-medium" style={{ color: 'var(--brand-primary, #0147a0)' }}>· {r.viagem.horario}</span>}
                            </div>
                          </>
                        )}
                        {r.observacoes && <p className="text-slate-400 text-xs mt-1 italic truncate">{r.observacoes}</p>}
                      </div>
                      <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusCls[r.status] || 'bg-slate-100 text-slate-700'}`}>
                          {statusLabel[r.status] || r.status}
                        </span>
                        {r.valor && <span className="text-sm font-semibold text-slate-700">R$ {fmt(r.valor)}</span>}
                        {r.check_in && (
                          <span className="flex items-center gap-1 text-emerald-600 text-xs font-medium">
                            <CheckCircle className="w-3 h-3" /> Embarcou
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default function Clientes() {
  const { user } = useAuth();
  const { branding } = useTenant();
  const canWrite = user?.role !== 'financeiro';
  const showFicha = moduleEnabled(branding, 'ficha');
  const [clientes, setClientes] = useState([]);
  const [search, setSearch] = useState('');
  const [tipoFilter, setTipoFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [historicoId, setHistoricoId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [cpfDuplicado, setCpfDuplicado] = useState(null);
  const [checkingCpf, setCheckingCpf] = useState(false);
  const [fichaOpen, setFichaOpen] = useState(false);
  const cpfCheckRef = useRef(null);

  const ff = (k, v) => setForm(p => ({ ...p, ficha: { ...(p.ficha || {}), [k]: v } }));

  const closeModal = () => {
    setModalOpen(false); setEditingId(null); setForm(EMPTY);
    setCpfDuplicado(null); setCheckingCpf(false); setFichaOpen(false);
  };

  const handleDocumentoBlur = async () => {
    const digits = (form.documento || '').replace(/\D/g, '');
    if (!digits || digits.length < 11) { setCpfDuplicado(null); return; }
    if (cpfCheckRef.current) clearTimeout(cpfCheckRef.current);
    setCheckingCpf(true);
    try {
      const params = { documento: form.documento };
      if (editingId) params.excluir_id = editingId;
      const controller = new AbortController();
      const tid = setTimeout(() => controller.abort(), 6000);
      cpfCheckRef.current = tid;
      const { data } = await api.get('/clientes/verificar-documento', { params, signal: controller.signal });
      clearTimeout(tid);
      setCpfDuplicado(data.existe ? data.cliente : null);
    } catch (err) {
      if (err?.name !== 'CanceledError' && err?.code !== 'ERR_CANCELED') setCpfDuplicado(null);
    } finally { cpfCheckRef.current = null; setCheckingCpf(false); }
  };

  const handleDocChange = (val) => {
    setForm(p => ({ ...p, documento: formatarDocumento(val) }));
    if (cpfDuplicado) setCpfDuplicado(null);
  };

  const fetchClientes = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/clientes', { params: { search: search || undefined, tipo: tipoFilter || undefined } });
      setClientes(data);
    } catch { toast.error('Erro ao carregar clientes'); }
    finally { setLoading(false); }
  }, [search, tipoFilter]);

  useEffect(() => { fetchClientes(); }, [tipoFilter]);

  const openCreate = () => { setForm(EMPTY); setEditingId(null); setCpfDuplicado(null); setFichaOpen(false); setModalOpen(true); };
  const openEdit = (c) => { setForm({ ...c, documento: formatarDocumento(c.documento || '') }); setEditingId(c.id); setCpfDuplicado(null); setFichaOpen(!!(c.ficha && Object.values(c.ficha).some(Boolean))); setModalOpen(true); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingId) {
        await api.put(`/clientes/${editingId}`, form);
        toast.success('Cliente atualizado!');
      } else {
        await api.post('/clientes', form);
        toast.success('Cliente cadastrado!');
      }
      closeModal(); fetchClientes();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erro ao salvar'); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/clientes/${deleteId}`);
      toast.success('Cliente excluído'); setDeleteId(null); fetchClientes();
    } catch { toast.error('Erro ao excluir'); }
  };

  const handleExportCSV = async () => {
    setExporting(true);
    try {
      const res = await api.get('/clientes/export/csv', { params: { search: search || undefined, tipo: tipoFilter || undefined }, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv;charset=utf-8;' }));
      const a = document.createElement('a');
      a.href = url; a.setAttribute('download', 'clientes.csv');
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('CSV exportado!');
    } catch { toast.error('Erro ao exportar CSV'); }
    finally { setExporting(false); }
  };

  const inp = "w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--brand-primary)]/20 focus:border-[var(--brand-primary)]";

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Clientes</h1>
          <p className="text-slate-500 text-sm mt-0.5">{clientes.length} cliente(s) cadastrado(s)</p>
        </div>
        <div className="flex gap-2">
          <button onClick={handleExportCSV} disabled={exporting}
            className="flex items-center gap-2 border border-slate-300 text-slate-600 hover:bg-slate-50 font-medium px-4 py-2.5 rounded-lg text-sm disabled:opacity-60 transition-colors">
            <Download className="w-4 h-4" /> {exporting ? 'Exportando...' : 'Exportar CSV'}
          </button>
          {canWrite && (
            <button onClick={openCreate}
              className="flex items-center gap-2 text-white font-medium px-4 py-2.5 rounded-lg text-sm transition-colors"
              style={{ background: 'var(--brand-secondary, #d72d21)' }}>
              <Plus className="w-4 h-4" /> Novo Cliente
            </button>
          )}
        </div>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input value={search} onChange={e => setSearch(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && fetchClientes()}
              placeholder="Buscar por nome, email, CPF/CNPJ, telefone..."
              className="w-full pl-9 pr-4 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:border-[var(--brand-primary)]" />
          </div>
          <select value={tipoFilter} onChange={e => setTipoFilter(e.target.value)}
            className="px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none bg-white">
            <option value="">Todos os tipos</option>
            <option value="PF">Pessoa Física</option>
            <option value="PJ">Pessoa Jurídica</option>
          </select>
          <button onClick={fetchClientes}
            className="px-4 py-2 text-white rounded-lg text-sm font-medium transition-colors"
            style={{ background: 'var(--brand-primary, #0147a0)' }}>
            Buscar
          </button>
        </div>
      </div>

      {/* Tabela */}
      <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden shadow-sm">
        {loading ? (
          <div className="p-8 text-center text-slate-400 text-sm">Carregando...</div>
        ) : clientes.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-slate-400">
            <Users className="w-12 h-12 mb-3 opacity-20" />
            <p className="font-medium">Nenhum cliente encontrado</p>
            <p className="text-xs mt-1">Clique em "Novo Cliente" para começar</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Nome</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Tipo</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider hidden md:table-cell">Telefone</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider hidden lg:table-cell">Email</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider hidden lg:table-cell">Documento</th>
                  <th className="px-5 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {clientes.map((c) => (
                  <tr key={c.id} className="hover:bg-slate-50/60 transition-colors">
                    <td className="px-5 py-3.5 font-medium text-slate-800">{c.nome}</td>
                    <td className="px-5 py-3.5"><Badge tipo={c.tipo} /></td>
                    <td className="px-5 py-3.5 text-slate-500 hidden md:table-cell">{c.telefone || '—'}</td>
                    <td className="px-5 py-3.5 text-slate-500 hidden lg:table-cell truncate max-w-[180px]">{c.email || '—'}</td>
                    <td className="px-5 py-3.5 text-slate-500 hidden lg:table-cell">{c.documento || '—'}</td>
                    <td className="px-5 py-3.5">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => setHistoricoId(c.id)} title="Histórico"
                          className="p-1.5 rounded-lg text-slate-400 hover:text-orange-600 hover:bg-orange-50 transition-colors">
                          <History className="w-4 h-4" />
                        </button>
                        {canWrite && (
                          <>
                            <button onClick={() => openEdit(c)} title="Editar"
                              className="p-1.5 rounded-lg text-slate-400 hover:text-blue-600 hover:bg-blue-50 transition-colors">
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button onClick={() => setDeleteId(c.id)} title="Excluir"
                              className="p-1.5 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal criar/editar */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
              <h2 className="font-bold text-slate-800 text-lg">{editingId ? 'Editar Cliente' : 'Novo Cliente'}</h2>
              <button onClick={closeModal} className="p-1.5 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Nome completo *</label>
                <input value={form.nome} onChange={e => setForm(p => ({ ...p, nome: e.target.value }))} required autoFocus className={inp} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">Telefone</label>
                  <input value={form.telefone || ''} onChange={e => setForm(p => ({ ...p, telefone: e.target.value }))}
                    placeholder="(11) 99999-9999" className={inp} />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">Tipo</label>
                  <select value={form.tipo} onChange={e => setForm(p => ({ ...p, tipo: e.target.value }))} className={inp}>
                    <option value="PF">Pessoa Física</option>
                    <option value="PJ">Pessoa Jurídica</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Email</label>
                <input type="email" value={form.email || ''} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} className={inp} />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">CPF / CNPJ</label>
                <div className="relative">
                  <input value={form.documento || ''} onChange={e => handleDocChange(e.target.value)} onBlur={handleDocumentoBlur}
                    className={`${inp} pr-9 ${cpfDuplicado ? 'border-red-400 bg-red-50' : ''}`} />
                  <div className="absolute right-2.5 top-1/2 -translate-y-1/2">
                    {checkingCpf && <div className="w-4 h-4 border-2 border-[var(--brand-primary)] border-t-transparent rounded-full animate-spin" />}
                    {!checkingCpf && cpfDuplicado && <AlertCircle className="w-4 h-4 text-red-500" />}
                    {!checkingCpf && !cpfDuplicado && (form.documento || '').replace(/\D/g,'').length >= 11 && <CheckCircle className="w-4 h-4 text-emerald-500" />}
                  </div>
                </div>
                {cpfDuplicado && (
                  <div className="mt-2 rounded-xl border border-red-200 bg-red-50 p-3">
                    <div className="flex items-start gap-2.5">
                      <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <p className="text-sm font-semibold text-red-700">Este CPF já está cadastrado.</p>
                        <div className="mt-2 flex items-center gap-2 bg-white rounded-lg border border-red-100 px-3 py-2">
                          <div className="w-7 h-7 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                            <UserCheck className="w-3.5 h-3.5 text-red-600" />
                          </div>
                          <div>
                            <p className="text-sm font-bold text-slate-800">{cpfDuplicado.nome}</p>
                            {cpfDuplicado.telefone && <p className="text-xs text-slate-500">{cpfDuplicado.telefone}</p>}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Observações</label>
                <textarea value={form.observacoes || ''} onChange={e => setForm(p => ({ ...p, observacoes: e.target.value }))}
                  rows={2} className={inp + ' resize-none'} />
              </div>

              {showFicha && (
                <div className="border border-slate-200 rounded-xl overflow-hidden">
                  <button type="button" onClick={() => setFichaOpen(o => !o)}
                    className="w-full flex items-center justify-between px-4 py-2.5 bg-slate-50 hover:bg-slate-100 transition-colors">
                    <span className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                      <Activity className="w-4 h-4 text-slate-400" /> Ficha de aptidão física
                      <span className="text-xs font-normal text-slate-400">(opcional)</span>
                    </span>
                    <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${fichaOpen ? 'rotate-180' : ''}`} />
                  </button>
                  {fichaOpen && (
                    <div className="p-4 space-y-3">
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-semibold text-slate-600 mb-1">Condicionamento físico</label>
                          <select value={form.ficha?.condicionamento || ''} onChange={e => ff('condicionamento', e.target.value)} className={inp + ' bg-white'}>
                            <option value="">— Não informado</option>
                            {CONDICIONAMENTO.map(c => <option key={c} value={c}>{c}</option>)}
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-slate-600 mb-1">Plano de saúde</label>
                          <input value={form.ficha?.plano_saude || ''} onChange={e => ff('plano_saude', e.target.value)} className={inp} />
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-slate-600 mb-1">Condições de saúde / alergias</label>
                        <textarea value={form.ficha?.condicoes_saude || ''} onChange={e => ff('condicoes_saude', e.target.value)} rows={2} className={inp + ' resize-none'} />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-slate-600 mb-1">Restrições alimentares</label>
                        <input value={form.ficha?.restricoes_alimentares || ''} onChange={e => ff('restricoes_alimentares', e.target.value)} className={inp} />
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-semibold text-slate-600 mb-1">Contato de emergência</label>
                          <input value={form.ficha?.contato_emergencia_nome || ''} onChange={e => ff('contato_emergencia_nome', e.target.value)} placeholder="Nome" className={inp} />
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-slate-600 mb-1">Telefone de emergência</label>
                          <input value={form.ficha?.contato_emergencia_telefone || ''} onChange={e => ff('contato_emergencia_telefone', e.target.value)} placeholder="(11) 99999-9999" className={inp} />
                        </div>
                      </div>
                      <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
                        <input type="checkbox" checked={!!form.ficha?.apto} onChange={e => ff('apto', e.target.checked)} className="rounded border-slate-300" />
                        Declara estar apto(a) para atividades físicas
                      </label>
                    </div>
                  )}
                </div>
              )}

              <div className="flex gap-3 pt-2">
                <button type="button" onClick={closeModal}
                  className="flex-1 px-4 py-2.5 border border-slate-300 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-50">
                  Cancelar
                </button>
                <button type="submit" disabled={saving || !!cpfDuplicado || checkingCpf}
                  className="flex-1 text-white rounded-lg text-sm font-semibold py-2.5 disabled:opacity-50 transition-colors"
                  style={{ background: 'var(--brand-secondary, #d72d21)' }}>
                  {checkingCpf ? 'Verificando CPF...' : saving ? 'Salvando...' : editingId ? 'Atualizar' : 'Cadastrar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {historicoId && <HistoricoModal clienteId={historicoId} onClose={() => setHistoricoId(null)} />}

      {deleteId && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6">
            <h3 className="font-bold text-slate-800 text-lg mb-2">Confirmar exclusão</h3>
            <p className="text-slate-500 text-sm mb-5">Esta ação não pode ser desfeita.</p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteId(null)} className="flex-1 px-4 py-2.5 border border-slate-300 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-50">Cancelar</button>
              <button onClick={handleDelete} className="flex-1 bg-red-500 hover:bg-red-600 text-white rounded-lg text-sm font-semibold py-2.5">Excluir</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
